<script setup lang="ts">
import type { ProductWithDisplayTags } from "#shared/types/product-display-tag";
import type { StoreProductBundle } from "#shared/types/product-bundle";
import { FeatureCarouselNavigation } from "~/features/carousel-navigation";
import ProductCard from "~/widgets/products-grid/ui/product-card.vue";
import type { KeenSliderInstance } from "keen-slider";
import { useKeenSlider } from "keen-slider/vue.es";
import "keen-slider/keen-slider.min.css";

type ProductBundleWithProducts = StoreProductBundle & {
    products: ProductWithDisplayTags[];
};

const props = defineProps<{
    bundle: ProductBundleWithProducts;
}>();

const currentSlide = ref(0);
const maxSlide = ref(0);
const isReady = ref(false);

const syncSliderState = (instance: KeenSliderInstance) => {
    currentSlide.value = instance.track.details.rel;
    maxSlide.value = instance.track.details.maxIdx;
};

const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    selector: (element) => Array.from(element.children) as HTMLElement[],
    slides: {
        perView: 2,
        spacing: 16,
    },
    breakpoints: {
        "(min-width: 1024px)": {
            slides: {
                perView: 4,
                spacing: 16,
            },
        },
    },
    created(instance) {
        isReady.value = true;
        syncSliderState(instance);
    },
    slideChanged: syncSliderState,
    optionsChanged: syncSliderState,
    updated: syncSliderState,
});

const pagination = computed(() =>
    Array.from({ length: maxSlide.value + 1 }, (_, index) => index),
);
const canSlide = computed(() => isReady.value && maxSlide.value > 0);
const isBeginning = computed(() => currentSlide.value === 0);
const isEnd = computed(() => currentSlide.value === maxSlide.value);
const productIdsKey = computed(() =>
    props.bundle.products.map((product) => product.id).join(","),
);

watch(productIdsKey, async () => {
    await nextTick();
    slider.value?.update(undefined, 0);
});
</script>

<template>
    <section class="mt-8 lg:mt-14">
        <h2
            class="mb-4 font-serif text-xl font-medium uppercase lg:mb-9 lg:text-[1.75rem]"
        >
            {{ bundle.name }}
        </h2>

        <div class="relative">
            <div
                ref="sliderRef"
                class="bundle-products-slider keen-slider"
                :class="{ 'is-ready': isReady }"
                role="region"
                :aria-label="`Комплект ${bundle.name}`"
            >
                <div
                    v-for="(product, index) in bundle.products"
                    :key="product.id"
                    class="keen-slider__slide"
                    role="group"
                    :aria-label="`${index + 1} из ${bundle.products.length}`"
                >
                    <ProductCard
                        :product="product"
                        :analytics-list="`Комплект: ${bundle.name}`"
                        :analytics-position="index + 1"
                        :image-width="384"
                        image-sizes="sm:50vw lg:25vw 2xl:384px"
                        first-image-loading="lazy"
                        first-image-fetch-priority="low"
                        :allow-image-swipe="false"
                    />
                </div>
            </div>

            <FeatureCarouselNavigation
                :visible="canSlide"
                color-scheme="light"
                horizontal-offset="inside"
                previous-label="Предыдущие товары комплекта"
                next-label="Следующие товары комплекта"
                :previous-disabled="isBeginning"
                :next-disabled="isEnd"
                class="left-0 w-full -translate-y-10 px-3"
                @previous="slider?.prev()"
                @next="slider?.next()"
            />
        </div>
    </section>
</template>

<style scoped>
.bundle-products-slider:not(.is-ready) {
    gap: 1rem;
}

.bundle-products-slider > .keen-slider__slide {
    min-width: calc((100% - 1rem) / 2);
    max-width: calc((100% - 1rem) / 2);
}

@media (min-width: 1024px) {
    .bundle-products-slider > .keen-slider__slide {
        min-width: calc((100% - 3rem) / 4);
        max-width: calc((100% - 3rem) / 4);
    }
}
</style>
